// Lightweight translation helper that supports both flat and nested keys
// Usage:
//   import { dt } from '../utils/i18nHelpers';
//   const title = dt(t, 'sideMenu.createVersion.title');
// It will try the provided key, and if missing, attempt a nested/flat fallback.

/**
 * Dual-translation helper: tries both flat and nested forms of a key.
 * Example key: 'sideMenu.createVersion.title'
 * - Flat: 'sideMenu.createVersion.title'
 * - Nested: { sideMenu: { createVersion: { title: '...' } } }
 * @param {Function} t - i18next t function
 * @param {string} key - dot-notated key
 * @param {object} [options] - i18next options (values, defaultValue, etc.)
 * @returns {string} resolved translation or the key if unresolved
 */
export function dt(t, key, options = {}) {
  if (typeof t !== 'function' || !key) return key || '';

  // First attempt: as-is
  let res = t(key, { ...options, defaultValue: undefined });
  if (res && res !== key) return res;

  // Build fallback forms: attempt swapping between flat and nested
  // If keySeparator is disabled, some setups store flat keys only.
  // We'll try to resolve by joining/splitting on dots.
  const parts = key.split('.');
  if (parts.length > 1) {
    // Try namespaced form explicitly if default namespace differs
    if (options && options.ns) {
      res = t(`${options.ns}:${key}`, { ...options, defaultValue: undefined });
      if (res && res !== `${options.ns}:${key}`) return res;
    }

    // Try progressively shorter paths with object-style resolution
    // Example: sideMenu.createVersion.title → sideMenu.createVersion.title (already tried)
    // There isn't a universal way to invert object vs flat here, but we can try a few fallbacks.
    // 1) Try key without last segment as a direct key (rare, but safe):
    const withoutLast = parts.slice(0, -1).join('.');
    res = t(withoutLast, { ...options, defaultValue: undefined });
    if (res && res !== withoutLast) return res;
  }

  // Final: return original key or provided defaultValue
  if (options && typeof options.defaultValue === 'string') return options.defaultValue;
  return key;
}

/**
 * React-friendly wrapper: returns a function that uses dt with bound t.
 * Usage inside components:
 *   const { t } = useTranslation();
 *   const dtx = useDT(t);
 *   dtx('sideMenu.createVersion.title');
 */
export function useDT(t) {
  return (key, options = {}) => dt(t, key, options);
}

/**
 * Drop-in replacement for useTranslation that returns a dual-key-aware t.
 * Usage:
 *   const { t } = useDualTranslation(); // behaves like useTranslation but with dt
 */
export function useDualTranslation() {
  // Lazy import to avoid hard dependency here
  // eslint-disable-next-line global-require
  const { useTranslation } = require('react-i18next');
  const { t: baseT, i18n } = useTranslation();
  const dualT = (key, options = {}) => dt(baseT, key, options);
  return { t: dualT, i18n };
}
